<template>
  <PageMeta :meta="pageMeta" />
  <MainContent>
    <CmsAccessWall
      v-if="accessDenied"
      :title="post.title"
      :reason="accessDenied"
    />

    <article
      v-else-if="post"
      class="blog-detail-page"
      :class="{ 'has-toc': showToc }"
    >
      <figure
        v-if="coverImage"
        class="cover-figure"
      >
        <img
          :src="coverImage"
          :alt="post.localisation.title"
          class="cover-image"
        >
        <figcaption
          v-if="coverImageAttribution"
          class="cover-caption"
        >
          {{ $gadTranslate('general_unsplash_attribution_by') }}
          <a
            :href="coverImageAttribution.authorUrl"
            target="_blank"
            rel="noopener noreferrer"
          >{{ coverImageAttribution.author }}</a>
          {{ $gadTranslate('general_unsplash_attribution_on') }}
          <a
            :href="coverImageAttribution.unsplashUrl"
            target="_blank"
            rel="noopener noreferrer"
          >Unsplash</a>
        </figcaption>
      </figure>

      <GadBreadcrumb
        :items="breadcrumbItems"
        class="blog-breadcrumb"
      />
      <GoogleBreadcrumbJson :items="breadcrumbItems" />
      <GoogleBlogPostingJson :post="post" />

      <h1 class="title">
        {{ post.localisation.title }}
      </h1>

      <div
        v-if="chips.length > 0"
        class="chips"
      >
        <span
          v-for="chip in chips"
          :key="chip.term"
          class="chip"
        >
          {{ $gadTranslate(chip.term) }}
        </span>
      </div>

      <p
        v-if="post.localisation.excerpt"
        class="lead"
      >
        {{ post.localisation.excerpt }}
      </p>

      <div
        v-if="post.author?.name || post.publishedAt"
        class="author-block"
      >
        <div class="author-avatar">
          <AppImage
            v-if="usePlatformLogo"
            format="platform_logo"
            :path="platformLogoPath"
            :updated="platform.updated"
            :alt="post.author?.name || ''"
          />
          <img
            v-else-if="teamAvatarSrc"
            :src="teamAvatarSrc"
            :alt="post.author?.name || ''"
            class="team-photo"
          >
          <img
            v-else
            :src="authorPlaceholder"
            alt=""
          >
        </div>
        <div class="author-info">
          <BlogAuthorName
            v-if="post.author?.name"
            class="author-name"
            :name="post.author.name"
            :person-name="post.author.personName"
          />
          <span
            v-if="post.publishedAt"
            class="author-date"
          >{{ $gadTranslate('general_published') }}: {{ $gadDate(post.publishedAt, 'longDate') }}</span>
        </div>
        <div class="read-time">
          <i class="fa fa-clock" />
          <span>{{ $gadTranslate('page_blog_detail_read_time', { minutes: readTimeMinutes }) }}</span>
        </div>
      </div>

      <!-- Placed before the body so that on narrow screens, where it renders as a
           collapsible accordion, it sits above the article. On wide screens the grid
           below positions it in its own column to the right regardless of source order. -->
      <ClientOnly>
        <FloatingTableOfContents
          v-if="showToc"
          content-selector=".blog-detail-body"
          :share-title="post.localisation.title"
          :share-subtitle="post.author?.name || null"
          :share-image-url="coverImage"
          :share-email-subject="shareEmailSubject"
          :share-email-body="shareEmailBody"
          :share-disabled="preview"
          :edit-path="editPath"
        />
      </ClientOnly>

      <div class="blog-body-main">
        <!-- Rendered with our own transform + the shared RTE sanitizer (which allows
             img/figure/figcaption) rather than the global v-dompurify-html directive,
             whose stricter allowlist strips images. -->
        <div
          v-if="bodyHtml"
          class="rich-text body blog-detail-body"
          v-html="bodyHtml"
        />
      </div>

      <div class="bottom-share">
        <ShareButton
          :title-to-share="post.localisation.title"
          :subtitle-one="post.author?.name || null"
          :thumbnail-url="coverImage"
          :email-subject="shareEmailSubject"
          :email-body="shareEmailBody"
          :disabled="preview"
          share-button-text-key="general_share_blogpost"
          :show-share-icon="false"
        />
      </div>

      <nav
        v-if="navigation && (navigation.newer || navigation.older)"
        class="post-pagination"
      >
        <NuxtLink
          v-if="navigation.older"
          :to="tileHref(navigation.older)"
          class="pagination-link older"
        >
          <span class="direction">← {{ $gadTranslate('page_blog_detail_older_article') }}</span>
          <span class="post-title">{{ navigation.older.title }}</span>
        </NuxtLink>
        <NuxtLink
          v-if="navigation.newer"
          :to="tileHref(navigation.newer)"
          class="pagination-link newer"
        >
          <span class="direction">{{ $gadTranslate('page_blog_detail_newer_article') }} →</span>
          <span class="post-title">{{ navigation.newer.title }}</span>
        </NuxtLink>
      </nav>

      <section
        v-if="relatedPosts.length > 0"
        class="related-articles"
      >
        <h2 class="related-heading">
          {{ $gadTranslate('page_blog_detail_related_articles') }}
        </h2>
        <div class="related-grid">
          <BlogContentTile
            v-for="item in relatedPosts"
            :key="item.id"
            :post="item"
            :to="tileHref(item)"
          />
        </div>
      </section>
    </article>
  </MainContent>
</template>

<script setup>
/* global useAsyncData */
import { ref, computed } from 'vue'
import { PAGE_NOT_FOUND_STATUS_CODE } from 'give-a-day-shared/routes/statusCodes.mjs'
import { platformLogo } from 'give-a-day-shared/utils/s3Path.mjs'
import { CMS_TAXONOMY_TARGET_GROUP, CMS_TAXONOMY_BLOG_TYPES } from 'give-a-day-shared/data/enums/cmsTaxonomyEnums.mjs'
import { PATHS, getPathForLocaleWithFallback } from 'give-a-day-shared/routes/paths.mjs'
import { returnGadBaseUrl } from '@/functions/domains.js'
import { isSuperAuthor } from '~/functions/frontendAuthUtils.js'
import { mediaSliderFolderIdsInHtml, renderCmsBodyHtml } from '~/functions/cmsBodyHtml.js'
import authorPlaceholder from '~/assets/images/logo/g.png'

const nuxtApp = useNuxtApp()
const route = useRoute()

const blogId = ref(parseInt(route.params.id))
const post = ref(null)

// Preview mode: the post is opened from the editor's Preview button (?preview=true). The backend
// then returns the post regardless of its published status, gated by the editor's admin check.
const preview = route.query.preview === 'true'

const { data: fetchedPost } = await useAsyncData(`blog-detail-${blogId.value}${preview ? '-preview' : ''}`, async () => {
  try {
    return await nuxtApp.$getFromBackend(`/fetchPublicBlogPost/${blogId.value}`, preview ? { preview: true } : undefined)
  } catch (err) {
    if (err.statusCode === PAGE_NOT_FOUND_STATUS_CODE) {
      nuxtApp.$showPageNotFoundError()
      return null
    }
    throw err
  }
})
if (fetchedPost.value !== null) {
  post.value = fetchedPost.value
}

// A Private Content post the visitor may not read comes back as its title and a reason instead of the
// article, and the Access Wall is rendered in place of the cover image, author and body. The reason is
// 'login_required' for an anonymous visitor and 'not_linked' for a logged-in non-member.
const accessDenied = computed(() => post.value?.accessDenied ?? null)

// The older/newer buttons and related tiles. This is extra navigation around the post,
// so if it ever fails we still show the article rather than break the page.
const navigation = ref(null)
if (post.value && !accessDenied.value) {
  const { data: fetchedNavigation } = await useAsyncData(`blog-nav-${blogId.value}`, async () => {
    try {
      return await nuxtApp.$getFromBackend(`/fetchPublicBlogPostNavigation/${blogId.value}`)
    } catch {
      return null
    }
  })
  navigation.value = fetchedNavigation.value
}

const relatedPosts = computed(() => navigation.value?.related ?? [])

// Builds the link to another blog post, reusing the "/blog" overview path and adding
// the post's own slug and id (the blog detail route is /blog/:slug/:id).
const tileHref = item => `${backToBlogHref.value}/${item.slug}/${item.id}`

// The taxonomy terms we recognise, keeping Target Group chips before Blog Type chips.
// Terms we don't recognise are ignored.
const KNOWN_TERM_KEYS = new Set([...Object.values(CMS_TAXONOMY_TARGET_GROUP), ...Object.values(CMS_TAXONOMY_BLOG_TYPES)].map(t => t.key))
const TARGET_GROUP_KEYS = Object.values(CMS_TAXONOMY_TARGET_GROUP).map(t => t.key)

const chips = computed(() => {
  if (!post.value) return []
  const terms = post.value.taxonomyTerms ?? []
  return terms
    .filter(term => KNOWN_TERM_KEYS.has(term))
    .sort((a, b) => Number(TARGET_GROUP_KEYS.includes(b)) - Number(TARGET_GROUP_KEYS.includes(a)))
    .map(term => ({ term }))
})

// The author avatar: a platform-authored post shows that platform's logo, while a globally
// published (super admin) post has no per-person photo yet and falls back to a brand
// placeholder. Non-global posts are only shown on their own platform, so the current
// platform in global state is the post's platform.
const platform = computed(() => nuxtApp.$globalState?.platform || null)
const usePlatformLogo = computed(() => !post.value?.isGlobal && !!platform.value?.avatarExistsOnS3)
const platformLogoPath = computed(() => (platform.value ? platformLogo(platform.value.id) : null))

// A super-admin (global) post by a known team member shows that person's photo. The backend
// resolves the author to a stable avatarKey (no email is exposed); the photos are static
// files under frontend/public/images/team, so only the one shown is ever fetched.
const teamAvatarSrc = computed(() => {
  const key = post.value?.author?.avatarKey
  return key ? `/images/team/${key}.webp` : null
})

const coverImage = computed(() => post.value?.localisation?.coverImage || null)

// The Edit shortcut in the table of contents is offered only to the post's own author, linking
// straight to this content's editor. Other admins who could technically edit it are deliberately
// not offered it here. Super authors edit under the super-admin path; everyone else under the
// platform path.
const editPath = computed(() => {
  const user = nuxtApp.$auth?.user
  if (!user || !post.value?.author || user.id !== post.value.author.id) return null
  const paths = isSuperAuthor(user) ? PATHS.superAdminEditBlogrollContent : PATHS.editBlogrollContent
  const template = getPathForLocaleWithFallback(paths, { code: nuxtApp.$i18n.locale.value })
  return nuxtApp.$findPath(template, { id: blogId.value })
})

// The "share via email" mail. Built from one key per paragraph rather than a single key with
// newlines in it, because the translations come from a spreadsheet where line breaks don't
// survive. The share button appends the post URL below the body.
const shareEmailSubject = computed(() => nuxtApp.$gadTranslate('page_blog_detail_share_email_subject', { title: post.value?.localisation?.title }))

const shareEmailBody = computed(() => {
  const excerpt = post.value?.localisation?.excerpt
  const paragraphs = [
    nuxtApp.$gadTranslate('page_blog_detail_share_email_body_greeting'),
    nuxtApp.$gadTranslate('page_blog_detail_share_email_body_intro'),
    excerpt ? `${post.value?.localisation?.title}\n${excerpt}` : post.value?.localisation?.title,
    nuxtApp.$gadTranslate('page_blog_detail_share_email_body_outro'),
  ]
  return paragraphs.join('\n\n')
})

// The featured image's caption is its Unsplash attribution (stored as JSON). Manually
// uploaded covers have no attribution, so nothing is shown beneath them.
const coverImageAttribution = computed(() => {
  const raw = post.value?.localisation?.coverImageAttribution
  if (!raw) return null
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
})

/**
 * A slider shows one gallery folder. Its images are asked for at render time rather than stored with the post,
 * because the level of a folder can be narrowed long after the post was written: a folder that no longer reaches as
 * far as this page is refused, and the slider renders as nothing at all.
 */
const { data: sliderBlocks } = await useAsyncData(`blog-sliders-${blogId.value}`, async () => {
  const blocks = {}
  for (const folderId of mediaSliderFolderIdsInHtml(post.value?.localisation?.contentHtml)) {
    try {
      blocks[folderId] = await nuxtApp.$getFromBackend('/fetchMediaSliderBlock', { contentId: blogId.value, folderId })
    } catch {
      // Refused, or gone. Either way the post itself still renders.
    }
  }
  return blocks
}, { default: () => ({}) })

const bodyHtml = computed(() => renderCmsBodyHtml(post.value?.localisation?.contentHtml, {
  unsplashBy: nuxtApp.$gadTranslate('general_unsplash_attribution_by'),
  unsplashOn: nuxtApp.$gadTranslate('general_unsplash_attribution_on'),
  sliderBlocks: sliderBlocks.value ?? {},
}))

// Estimated reading time, shown beside the author. Words are counted off the plain text of
// the stored body HTML (tags stripped) and divided by an average adult reading speed of 225
// words per minute, rounded up so even a very short post reads as "1 min read".
const WORDS_READ_PER_MINUTE = 225
const readTimeMinutes = computed(() => {
  const html = post.value?.localisation?.contentHtml
  if (!html) return 1
  const words = html.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean)
  return Math.max(1, Math.ceil(words.length / WORDS_READ_PER_MINUTE))
})

// The table of contents is only useful — and its right-hand column only worth reserving —
// when the body actually has H2/H3 headings to list. Checked on the stored HTML so it is
// known during SSR and the layout doesn't shift once the component mounts client-side.
const showToc = computed(() => {
  if (!post.value?.showTableOfContents) return false
  const html = post.value?.localisation?.contentHtml
  return !!html && /<h[23][\s>]/i.test(html)
})

// Links back to the blog overview page by taking the current URL and removing the
// last two parts (the slug and the id), which leaves the "/blog" overview URL.
// A manually-typed trailing slash adds an empty final segment, so drop it first;
// otherwise splice(-2) would strip the id and the empty segment but keep the slug.
const backToBlogHref = computed(() => {
  const segments = route.path.split('/')
  if (segments[segments.length - 1] === '') segments.pop()
  segments.splice(-2)
  return segments.join('/') || '/'
})

// Three-level breadcrumb shown above the title: Home › Blog overview › this post.
// Routes are locale-relative (unprefixed); GadLink adds the locale/platform prefix.
// The last item uses seoRoute (not route) so the breadcrumb ld+json schema can include
// the current page's URL, while GadBreadcrumb still renders it as plain (non-link) text.
const breadcrumbItems = computed(() => [
  { label: nuxtApp.$gadTranslateUpper('nav_general_home'), route: '/' },
  { label: nuxtApp.$gadTranslateUpper('page_blog_archive_title_short'), route: '/blog' },
  {
    label: nuxtApp.$gadTranslateUpper('page_blog_breadcrumb_blog_post'),
    seoLabel: post.value?.localisation?.title || '',
    seoRoute: '/blog/:slug/:id',
    params: { slug: post.value?.localisation?.slug, id: blogId.value },
  },
])

const pageMeta = computed(() => {
  if (!post.value) {
    return { title: '', description: '', image: '' }
  }
  // Private content is never indexed, whether or not this particular viewer may read it: the crawler
  // that follows the same link is anonymous either way, and would meet the Access Wall.
  if (accessDenied.value) {
    return { title: post.value.title, description: '', image: '', noIndex: true }
  }
  const meta = {
    title: post.value.localisation.metaTitle || post.value.localisation.title,
    description: post.value.localisation.metaDescription || post.value.localisation.excerpt || '',
    image: coverImage.value || '',
    ogTitle: post.value.localisation.title,
    ogDescription: post.value.localisation.excerpt || null,
    noIndex: post.value.isPrivate === true,
    // The post has its own slug per language, so the language toggle and the alternate urls
    // must swap the slug along with the locale.
    urlParamsWithLocale: post.value.pathsWithFullLocale,
  }
  // A globally-authored (super admin) post is reachable on every platform's blog as well as
  // the main site, all serving the same content. Point the canonical at the main-site URL
  // (platformUrl: null forces the unprefixed path) so search engines consolidate those
  // duplicates onto one authoritative page. Platform-owned posts keep the default
  // self-referencing canonical.
  if (post.value.isGlobal) {
    meta.customCanonicalUrl = returnGadBaseUrl() + nuxtApp.$findPath('/blog/:slug/:id', {
      platformUrl: null,
      slug: post.value.localisation.slug,
      id: blogId.value,
    })
  }
  return meta
})
</script>

<style lang="scss" scoped>
@import '~assets/styles/variables/sizes';
@import '~assets/styles/variables/colors';

.blog-detail-page {
    max-width: 800px;
    padding: 0 30px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;

  .cover-figure {
    width: 100%;
    max-width: 800px;
    margin: 0 auto 1rem auto;
    padding: 0;
  }

  .cover-image {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    object-position: center;
    display: block;
    border-radius: 8px;
  }

  .cover-caption {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: #666;
    text-align: left;

    a {
      color: inherit;
      text-decoration: underline;
    }
  }

  // Images inside the rendered blog body. The content is injected via v-html, so these
  // rules need :deep() to reach it. Sizes mirror the editor (small/medium/large/full).
  .blog-detail-body :deep(.blog-image) {
    margin: 24px auto;

    img {
      display: block;
      // Fill the figure, whose width is set by the size class below (small/medium/large/full),
      // exactly like the editor does. Height follows the photo's natural aspect ratio.
      width: 100%;
      height: auto;
      margin: 0;
      border-radius: 8px;
    }

    figcaption {
      margin-top: 8px;
      font-size: 0.85rem;
      color: $secondary;
      text-align: center;

      a {
        color: inherit;
        text-decoration: underline;
      }
    }

    &.blog-image-small  { width: 25%; }
    &.blog-image-medium { width: 50%; }
    &.blog-image-large  { width: 75%; }
    &.blog-image-full   { width: 100%; }

    &.blog-image-align-left   { margin-left: 0; margin-right: auto; }
    &.blog-image-align-center { margin-left: auto; margin-right: auto; }
    &.blog-image-align-right  { margin-left: auto; margin-right: 0; }
  }

  // Quote block. Overrides the plainer global .rich-text blockquote style with a centred
  // pull-quote: larger italic text opened and closed by decorative marks in the accent
  // colour, sitting inline against the first and last characters of the quote. Injected
  // via v-html, so it needs :deep() to reach.
  .blog-detail-body :deep(blockquote) {
    position: relative;
    max-width: 90%;
    margin: 40px auto;
    padding: 8px 0 0;
    border-left: none;
    text-align: center;
    font-size: 1.4rem;
    font-style: italic;
    line-height: 1.5;
    color: $secondary;

    // Drop the global marks, which sit on the blockquote itself rather than around the text.
    &::before,
    &::after {
      content: none;
    }

    p {
      margin: 0;
    }

    // Pressing Enter at the end of a quote leaves an empty paragraph inside the blockquote, which
    // would otherwise read as a blank line under the quote.
    p:empty {
      display: none;
    }

    p:first-of-type::before,
    p:not(:empty):not(:has(~ p:not(:empty)))::after {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 3rem;
      line-height: 0;
      vertical-align: -0.7rem;
      color: $primary;
    }

    p:first-of-type::before {
      content: '“';
      margin-right: 6px;
    }

    // The last paragraph that has text, rather than the last paragraph: a trailing empty one is
    // hidden above, and hanging the closing mark off it would hide the mark along with it.
    p:not(:empty):not(:has(~ p:not(:empty)))::after {
      content: '”';
      margin-left: 6px;
    }
  }

  // Tables: a subtle header background, visible borders, and a horizontal scroll once there
  // are too many columns to fit. The columns size to their content and never drop below
  // $table-cell-min-width, so on a phone the table grows past the container and the wrapper
  // scrolls sideways instead of squeezing every column down to a few characters per line.
  // Injected via v-html, so it needs :deep() to reach.
  // A slider is a strip that scrolls and snaps, which the browser does on its own - no script, no library
  .blog-detail-body :deep(.blog-media-slider) {
    display: flex;
    gap: 8px;
    margin: 24px 0;
    overflow-x: auto;
    scroll-snap-type: x mandatory;

    .blog-media-slide {
      flex: 0 0 auto;
      max-height: 320px;
      scroll-snap-align: start;
    }
  }

  .blog-detail-body :deep(.blog-table-wrapper) {
    margin: 24px 0;
    overflow-x: auto;

    table {
      border-collapse: collapse;
      table-layout: auto;
      width: auto;
      min-width: 100%;

      td, th {
        border: 1px solid #d1d5db;
        padding: 8px 12px;
        vertical-align: top;
        min-width: $table-cell-min-width;
        word-break: break-word;

        > p { margin: 0; padding: 0; }
      }

      th {
        background: #f3f4f6;
        font-weight: 600;
        text-align: left;
      }
    }
  }

  // The shared breadcrumb centres itself; left-align it to sit with the article content.
  .blog-breadcrumb {
    margin-bottom: 8px;

    :deep(.justify-center) {
      justify-content: flex-start;
    }
  }

  .title {
    margin: 0 1rem 1rem 0;
    // On a platform, render the title in that platform's primary colour. The variable is
    // only defined on platform pages (set on <body> in AppContainer), so off-platform the
    // colour simply inherits the default heading colour.
    color: var(--primary-color);
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;

    .chip {
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 0.85rem;
      background-color: #f3f4f6;
      color: #374151;
    }
  }

  .author-block {
    display: flex;
    align-items: center;
    // On mobile the read-time wraps onto its own line beneath the avatar + info row
    // (see .read-time below); from $mobile-width up it sits inline on the right.
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
    padding: 12px 16px;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
  }

  .read-time {
    // Mobile: drop to a new full-width line, centred under the avatar + info.
    flex-basis: 100%;
    justify-content: center;
    display: flex;
    align-items: center;
    gap: 6px;
    color: $secondary;
    font-size: 0.9rem;
    white-space: nowrap;

    i {
      font-size: 1rem;
    }
  }

  .author-avatar {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    overflow: hidden;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;

    // The platform logo comes through AppImage's own wrapper; make it and the placeholder
    // <img> fill the circle. Logos are contained (not cropped) so they stay fully visible.
    :deep(.app-image-wrapper) {
      width: 100%;
      height: 100%;
      display: flex;
    }

    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 6px;
    }

    // A team member's head photo should fill the circle (cropped), not be contained
    // with padding the way logos and the brand placeholder are.
    .team-photo {
      object-fit: cover;
      padding: 0;
    }
  }

  .author-info {
    display: flex;
    flex-direction: column;
    line-height: 1.3;

    .author-name {
      font-weight: bold;
    }

    .author-date {
      color: $secondary;
      font-size: 0.9rem;
    }
  }

  // The excerpt/standfirst. An accent bar on the left plus italic text sets it apart from
  // the body, which never uses a left border, making it read clearly as a summary. The bar
  // takes the platform primary colour (only defined on platform pages; inherits otherwise).
  .lead {
    font-size: 0.9rem;
    font-style: italic;
    line-height: 1.6;
    color: #475569;
    margin-bottom: 28px;
    padding-left: 20px;
    border-left: 4px solid var(--primary-color);
  }

  .body {
    word-break: break-word;
  }

  .post-pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 48px;

    .pagination-link {
      flex: 1 1 240px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 16px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 8px;
      text-decoration: none;
      color: inherit;

      &:hover {
        border-color: $primary;
      }

      &.newer {
        text-align: right;
      }

      .direction {
        color: $secondary;
        font-size: 0.85rem;
      }

      .post-title {
        font-weight: 600;
      }
    }
  }

  .related-articles {
    margin-top: 48px;

    .related-heading {
      margin-bottom: 16px;
    }

    .related-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 32px 24px;
    }
  }

  .bottom-share {
    margin-top: 40px;
    padding: 24px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    :deep(.share-btn) {
      font-size: 1.1rem;
    }
  }
}

@media (min-width: $mobile-width) {
  .blog-detail-page {
    .lead {
      font-size: 1.2rem;
    }

    .related-articles .related-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

// Above small-mobile width the read-time returns to the same line as the avatar + info,
// pushed to the far right. Only true phones (below $mobile-width-small) get the stacked,
// centred read-time line.
@media (min-width: $mobile-width-small) {
  .blog-detail-page .read-time {
    flex-basis: auto;
    justify-content: flex-start;
    margin-left: auto;
  }
}

// On mobile the layout already adds a side gutter, so drop the article's own padding to give the text more width.
@media (max-width: $mobile-width) {
  .blog-detail-page {
    padding: 0;
  }
}

// On wide screens a post with a table of contents becomes a two-column layout: the
// article (capped at the readable 800px) on the left and the TOC in its own column to
// the right, outside the text width. The whole block is centred. The TOC column spans
// the full article height so its sticky inner block has room to follow the scroll.
// Below this width there isn't room for a side column, so the TOC falls back to the
// accordion above the article (handled inside FloatingTableOfContents).
@media (min-width: $mobile-width-wide) {
  .blog-detail-page.has-toc {
    max-width: 1160px;
    display: grid;
    grid-template-columns: minmax(0, 800px) 240px;
    column-gap: 40px;
    justify-content: center;
    align-items: start;

    > * {
      grid-column: 1;
      min-width: 0;
    }

    > .floating-toc {
      grid-column: 2;
      // Span every row so the column reaches the full article height, giving the sticky
      // inner block room to follow the scroll to the bottom. `1 / -1` only spans the
      // explicit grid, but our rows are implicit (no grid-template-rows), so it would
      // stop after the first row — hence a large explicit span. Empty extra rows are
      // zero-height, so this adds no layout.
      grid-row: 1 / span 99;
      align-self: stretch;
    }
  }
}
</style>
